// Chats: one list for both places a conversation lives (module 09 M16; module 10 U85; B599).
//
// Drawn as design 5 of screens/2026-09-20-chats/proposed-chats.html, which Mike chose on 2026-09-20
// ("Go with 5, but use 'External ⚠️' rather than outside"). Cobrain's own chats and the person's Teams
// conversations are in one list, each marked with where it lives, and any conversation with somebody
// who is not at this company is marked External ⚠️ and drawn in amber — his reason, in his words:
// "in case someone puts something in chat that should not have".
//
// WHAT IS READ WHERE: the Cobrain half is read live, through the messenger's own API, so a chat started
// a second ago is in the list and its unread count is the real one. The Teams half comes from the ready-
// made cards (migration 137), because reading it live cost 36 seconds. A Teams conversation is read-only
// here until the send-to-Teams round trip is built (M16).
//
// THE FILTER BAR (module 10 U228; pipeline F5, B1957): the bar every list wears, with the four chips kept beside its
// drop-down, By company: the companies of the people outside the team, which the list route names for each
// conversation in one read. The find, the chip and the company are kept in the address, and carried when a
// conversation is opened, so a reload, Back or a link draws the same list.

import { createPortal } from 'react-dom';
import { EmojiPicker } from './EmojiPicker.js';
import { ActionButton, ActionInput, ActionLink } from '../parts/ActionButton.js';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { ApiError, Unauthenticated, type Store } from '../api.js';
import { ChatView, NewChat } from '../Messenger.js';
import { LIVE_BEAT_MS, useLiveRefresh } from '../live.js';
import { messenger, type Me } from '../messenger/api.js';
import { initials, whenWords } from '../messenger/format.js';
import { stillSending } from './pending.js';
import { looksLikeAddress, moveHighlight, offer } from './pick.js';
import { teamsChatLink } from './teams-chat-link.js';
import { useMessengerSocket } from '../messenger/ws.js';
import { FilterBar, matchesWords, tally } from '../parts/FilterBar.js';
import { oneOf, useFilterParams } from '../find/params.js';
import {
  chatsApi, type ChatRoster, type ChatRow, type ChatsView, type Colleague, type DirectoryAddress, type HistoryChoice,
  type MessageExtra, type MessageReaction, type StartedGroup, type TeamsFindView, type TeamsThreadView,
} from './api.js';
import { named, nameOf, outsiders, previewOf, titleOf } from './name.js';
import { chatCallSaid, openCallWindow, placeChatCall } from '../call/api.js';
import '../messenger.css';
import './chats.css';
import { LineOrName } from '../parts/CompanyLine.js';
import { useCompanyLines } from '../company/lines.js';
import { TeamsReplyPanel } from '../onboarding/TeamsReply.js';
import { useMessageEditor } from './useMessageEditor.js';
import { ComposerIcon } from './ComposerIcon.js';
import { FILE_TOO_BIG, MAX_FILE_BYTES, inlineImage, sendAttachment } from './attachments.js';
import { sizeWords } from '../messenger/format.js';

function messageOf(e: unknown): string {
  if (e instanceof Unauthenticated) throw e;
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : 'something went wrong';
}

const EXTERNAL_WORD = 'External';
/** The glyph is decoration. Read aloud on 286 rows, a chip and a band, it is noise, so it is hidden from
    screen readers and the word carries the meaning. */
const Warn = () => <span aria-hidden="true"> ⚠️</span>;

type Filter = 'all' | 'cobrain' | 'teams' | 'external';
const FILTERS: readonly Filter[] = ['all', 'cobrain', 'teams', 'external'];
const KEYS = ['q', 'show', 'company'] as const;

function Tag({ source }: { source: ChatRow['source'] }) {
  return <span className={source === 'teams' ? 'ch-src teams' : 'ch-src'}>{source === 'teams' ? 'TEAMS' : 'COBRAIN'}</span>;
}

function Line({ c, on, onOpen, myName, showSource }: { c: ChatRow; on: boolean; onOpen: () => void; myName: string | null; showSource: boolean }) {
  const { name, real } = titleOf(c, myName);
  return (
    <ActionButton reading type="button" className={`ch-c${c.hasExternal ? ' out' : ''}${on ? ' on' : ''}`} onClick={onOpen} aria-current={on ? 'true' : undefined}>
      <span className={c.hasExternal ? 'ch-av g' : 'ch-av'} aria-hidden="true">{real ? initials(name) : '·'}</span>
      <span className="ch-mid">
        {/* The name line carries the name and, when it matters, the amber mark -- nothing else. Live on
            2026-09-20 a source badge here left names as "Alex Rob..." on a 320px column. */}
        <span className="ch-nm">
          <span className="ch-who">{name}</span>
          {c.hasExternal ? <span className="ch-tag out">{EXTERNAL_WORD}<Warn /></span> : null}
        </span>
        <span className="ch-pv">{previewOf(c, myName)}</span>
      </span>
      <span className="ch-rt">
        {c.lastAt ? whenWords(c.lastAt) : ''}
        {/* Where it lives, only when the list holds both: on a list that is all Teams the word is on every
            row and tells nobody anything. */}
        {showSource ? <Tag source={c.source} /> : null}
        {c.unread > 0 ? <b aria-label={`${c.unread} unread`}>{c.unread > 99 ? '99+' : c.unread}</b> : null}
      </span>
    </ActionButton>
  );
}

/** The six quick reactions Teams offers beside its full picker. */
const REACTIONS: { name: string; glyph: string; says: string }[] = [
  { name: 'like', glyph: '👍', says: 'Like' },
  { name: 'heart', glyph: '❤️', says: 'Love' },
  { name: 'laugh', glyph: '😆', says: 'Laugh' },
  { name: 'surprised', glyph: '😮', says: 'Surprised' },
  { name: 'sad', glyph: '😢', says: 'Sad' },
  { name: 'angry', glyph: '😡', says: 'Angry' },
];

/** Quick reactions keep their named API values; the shared picker sends Unicode. */
function Reactions({ on, busy, onPick }: { on: boolean; busy: boolean; onPick: (reaction: string | { emoji: string }) => void }) {
  const [open, setOpen] = useState(false);
  const [picker, setPicker] = useState(false);
  const more = useRef<HTMLButtonElement>(null);
  const close = () => { setPicker(false); more.current?.focus(); };
  if (!on) return null;
  return (
    <span className="ch-react">
      <ActionButton type="button" className="ch-react-b" disabled={busy} aria-label="Like this message" title="Like" onClick={() => onPick('like')}>{REACTIONS[0]!.glyph}</ActionButton>
      <ActionButton type="button" className="ch-react-b more" disabled={busy} aria-expanded={open} aria-label="Other reactions" title="Other reactions" onClick={() => setOpen((v) => !v)}>&#8964;</ActionButton>
      {open ? (
        <span className="ch-react-menu" role="group" aria-label="Reactions">
          {REACTIONS.filter((r) => r.name !== 'like').map((r) => (
            <ActionButton key={r.name} type="button" className="ch-react-b" disabled={busy} title={r.says} aria-label={r.says}
              onClick={() => { setOpen(false); onPick(r.name); }}>{r.glyph}</ActionButton>
          ))}
        </span>
      ) : null}
      <ActionButton ref={more} type="button" className="ch-react-b" disabled={busy} aria-label="More reactions" title="More reactions"
        aria-haspopup="dialog" aria-expanded={picker} onClick={() => { setOpen(false); setPicker(v => !v); }}>+</ActionButton>
      {picker && !busy && more.current && createPortal(
        <div className="ch-reaction-picker"><EmojiPicker onPick={emoji => { close(); onPick({ emoji }); }} onClose={close} /></div>,
        more.current.closest('.ch-thread')!,
      )}
    </span>
  );
}

/**
 * Replying, from here. Mike, 2026-09-20: "Reply in teams is only reply, you reply inside cobrain, you do
 * not load up an exteral teams window." The grammar is the Cobrain messenger own composer, copied rather
 * than invented, so the two chat surfaces read the same.
 *
 * Nothing is sent that was not pressed for (D69), there is no draft and no retry, and a picture is the one
 * kind of file this consent can carry: a document has to be in SharePoint before Teams will take it, which
 * needs a permission Cobrain has not been given, so the page says so rather than offering a button that fails.
 */
/**
 * How often a conversation that is OPEN IN FRONT OF SOMEBODY asks again (B1410).
 *
 * Mike, 2026-09-21, both halves of the same fault: "When I send a message I get Sent to Teams ... But this
 * has to be instant?" and "When Renaldo responds, his responses do not appear unless I refresh the page."
 * The second was literal -- this pane read once when it mounted and never again, so a reply could sit in
 * the store for an hour and the page would not show it.
 *
 * Ten seconds, and the beat is allowed at all because the read was timed first: 471 ms as Mike through the
 * rails on his own store, which is the rule live.ts sets ("no beat on a read nobody has timed"). It costs
 * six reads a minute WHILE THE TAB IS IN FRONT and nothing at all when it is not.
 *
 * TEN SECONDS IS NOT THE WHOLE WAIT, and it is not where the remaining wait is. Cobrain answers from its
 * own store, and a message reaches that store when the Teams connector's next delta round runs: the rounds
 * themselves take 20 to 30 seconds, and the worker leaves up to DELTA_INTERVAL_SECONDS (60 on the droplet)
 * between them. So a colleague's reply lands here in about 40 seconds typically and 90 at worst, and this
 * beat is what makes those seconds the whole of it instead of "never, until you reload". Closing the last
 * minute of that is a change notification on /users/{id}/chats/getAllMessages, which pokes the connector
 * the moment Graph sees the message -- the machine for it already exists and is wired for mail alone.
 */
const THREAD_BEAT_MS = 10_000;

export function Composer({ store, threadId, name, external, onSent }: {
  store: string | null; threadId: string; name: string; external: boolean; onSent: (said: string) => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [notShared, setNotShared] = useState<string[]>([]);
  const [needConsent, setNeedConsent] = useState(false);
  const [fileConsent, setFileConsent] = useState(false);
  const [hadConsent, setHadConsent] = useState<boolean | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!file || !inlineImage(file)) { setPreview(null); return; }
    const url = URL.createObjectURL(file); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => {
    if (!needConsent) return;
    let live = true;
    chatsApi.consent().then(consent => { if (live) setHadConsent(consent.hadConsent); }).catch(error => {
      if (live) { setNeedConsent(false); setSaid(messageOf(error)); }
    });
    return () => { live = false; };
  }, [needConsent]);
  const pickFile = (picked: File) => {
    if (busy) return;
    if (picked.size > MAX_FILE_BYTES) { setSaid(FILE_TOO_BIG); return; }
    setSaid(null); setFileConsent(false); setFile(picked);
  };
  const editor = useMessageEditor({ text, setText, busy, placeholder: `Reply to ${name}…`, onSend: () => { void send(); }, onFile: pickFile });
  const send = async () => {
    const body = editor.message();
    if (busy || (!body.text && !file)) return;
    if (file && file.size > MAX_FILE_BYTES) { setSaid(FILE_TOO_BIG); return; }
    setBusy(true); setSaid(null); setNotShared([]); setFileConsent(false);
    try {
      if (file) setNotShared(await sendAttachment(store, threadId, file, body.text));
      else if (body.html !== undefined) await chatsApi.teamsReply(store, threadId, body.text, body.html);
      else await chatsApi.teamsReply(store, threadId, body.text);
      const carried = file ? (body.text ? `${body.text} (${file.name})` : file.name) : body.text;
      editor.clear(); setFile(null); onSent(carried);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        if (file && !inlineImage(file)) setFileConsent(true); else setNeedConsent(true);
      } else setSaid(messageOf(error));
    } finally { setBusy(false); }
  };
  const consentPanel = needConsent && hadConsent !== null;
  return <div className={external ? 'ch-comp out' : 'ch-comp'}>
    {consentPanel ? <TeamsReplyPanel hadConsent={hadConsent} next={`/chats/${threadId}`} onNotNow={() => setNeedConsent(false)} /> : null}
    <div hidden={consentPanel}>
      {file ? <div className="ch-attached">
        {preview ? <img className="ch-attached-i" src={preview} alt="" /> : null}
        <span className="ch-attached-n">{file.name} · {sizeWords(file.size)}</span>
        <ActionButton type="button" className="ch-attached-x" aria-label="Remove" title="Remove" disabled={busy} onClick={() => setFile(null)}>×</ActionButton>
      </div> : null}
      {editor.bar}
      <div className="ch-compose-row">
        {editor.field}
        {editor.controls}
        <ActionInput ref={fileRef} type="file" style={{ display: 'none' }} disabled={busy}
          onChange={e => { const picked = e.target.files?.[0]; if (picked) pickFile(picked); e.target.value = ''; }} />
        <ActionButton type="button" className="ch-compose-icon" disabled={busy} aria-label="Attach file" title="Attach file" onClick={() => fileRef.current?.click()}><ComposerIcon name="Attach file" /></ActionButton>
        <ActionButton type="button" className="btn small" disabled={busy || (!text.trim() && !file)} onClick={() => void send()}>{busy ? 'Sending…' : 'Send'}</ActionButton>
      </div>
      {said ? <p className="note ch-sendfail" role="alert">{said}</p> : null}
      {fileConsent ? <p className="note ch-consent" role="alert">Cobrain needs your permission once more before it can send a file as you.{' '}
        <ActionLink href={`/api/teams-consent/start?next=${encodeURIComponent(`/chats/${threadId}`)}`}>Allow it</ActionLink></p> : null}
      {notShared.length ? <p className="note" role="status">Sent. Not shared with: {notShared.join(', ')}.</p> : null}
    </div>
  </div>;
}

/**
 * Who is in the room (B911). Mike, 2026-09-21: "In the group settings you are missing the ability to see who is
 * in the group, to add people remove people."
 *
 * The roster comes from Teams when the conversation opens, not from the list's own "people": those are everyone who
 * has ever been on a message of this conversation, which includes anybody who has since left and misses anybody
 * who joined after the last one. This panel answers the question actually asked -- who is in it now.
 *
 * Adding and removing is not drawn as a button Cobrain cannot press. It is the conversation itself, opened where
 * the roster can be changed, which is the same choice the composer made about replying before replying was built.
 */
/**
 * Starting a group (B1223). Mike, 2026-09-21: "It opens a teams and cobrain group chat, but it all
 * stays within cobrain."
 *
 * The group is made in TEAMS, because that is where his colleagues are: ten have been invited to
 * Cobrain and not one has signed in, so a picker of Cobrain accounts is an empty list and always has
 * been. It then arrives here the way every other Teams conversation arrives and he reads and answers
 * it in Cobrain without opening Teams.
 *
 * A first message is required for groups created here. A Teams group with nothing said in it is shown
 * to nobody -- not in Teams, and not here, because a conversation in Cobrain is built from its
 * messages. Sending the first line is what makes the room real in both places. With an outside person,
 * Start hands the people and any first words to Teams; the person presses send there (REQ-2026-10-15-53).
 */
/**
 * The wait between "Teams made it" and "it is on your list" (B1223). Seconds to a couple of minutes:
 * the worker's delta pull, then the two-minute card pass. It is shown honestly rather than hidden
 * behind a spinner that says nothing, because the group DOES exist by now and a person who closes
 * this has lost nothing.
 *
 * It finds the new room by elimination -- the Teams conversation that was not on the list before --
 * rather than by matching its name, because two groups may share a name and a group need not have one.
 */
function GroupArriving({ made, onGiveUp }: { made: StartedGroup; onGiveUp: () => void }) {
  return (
    <div className="ch-new-group">
      <div className="ch-people-h"><b>{made.topic ?? 'New chat'}</b><ActionButton reading type="button" className="dotted ch-people-x" onClick={onGiveUp}>Close</ActionButton></div>
      {made.sent ? (
        <p className="ch-people-sub">
          Made in Teams with {made.members} {made.members === 1 ? 'person' : 'people'} in it, and your first message sent.
          Cobrain opens it here as soon as it reads it, usually within a couple of minutes.
        </p>
      ) : (
        <p className="note ch-error" role="alert">
          {made.error ?? 'The group was made in Teams, but the first message was not sent.'}
        </p>
      )}
      <p className="note">You can close this; nothing is lost. The chat is already in Teams.</p>
    </div>
  );
}

/**
 * New chat, as Teams makes one (BUGS.md M58, REQ-2026-10-16-22). Mike, 16 Oct 2026: "when you hit new, it wants to start
 * a new group, but it's just a new chat. [...] So I can add Connor. I can add will. So you don't need to define a group.
 * It becomes a group all by itself." So there is no name and no Start: a To: line, and the ordinary message box at the
 * foot. One colleague whose one-to-one chat Cobrain holds opens that chat; otherwise the first message sent makes it in
 * Teams, a one-to-one chat for one person and an unnamed group for two or more.
 */
export function NewTeamsGroup({ store, onClose, onMade, onOpen }: {
  store: string | null; onClose: () => void; onMade: (made: StartedGroup) => void;
  /** A conversation already here: the one-to-one chat with the one colleague on the To: line. */
  onOpen?: (threadId: string) => void;
}) {
  const [colleagues, setColleagues] = useState<Colleague[] | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [typed, setTyped] = useState('');
  const [pickedNames, setPickedNames] = useState<Record<string, string>>({});
  const [pickedOutside, setPickedOutside] = useState<Record<string, boolean>>({});
  const [handover, setHandover] = useState<string | null>(null);
  const [directory, setDirectory] = useState<{ address: string; store: string | null; result: DirectoryAddress | null } | null>(null);
  const checking = useRef<Promise<void>>(Promise.resolve());
  const address = looksLikeAddress(typed) ? typed.trim().toLowerCase() : null;
  // Which offered person the arrow keys are on. -1 is "none", which is what Enter reads to mean
  // "take what I typed", so a pasted address never has to be clicked.
  const [at, setAt] = useState(-1);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [needConsent, setNeedConsent] = useState(false);

  useEffect(() => {
    chatsApi.teamsColleagues(store)
      .then((r) => setColleagues(r.colleagues))
      .catch((e: unknown) => { setColleagues([]); setSaid(messageOf(e)); });
  }, [store]);

  const toggle = (address: string) =>
    setPicked((p) => (p.includes(address) ? p.filter((x) => x !== address) : [...p, address]));

  // WHY (2026-10-15, REQ-2026-10-15-53, Mike's words there): he could not add an outside address to a new chat.
  // His recent colleagues are not the directory. Check the address itself;
  // wait for the previous request to finish, and discard its answer if the box or store has changed meanwhile.
  useEffect(() => {
    let live = true;
    setDirectory(null);
    if (!address) return;
    const timer = setTimeout(() => {
      checking.current = checking.current.then(async () => {
        if (!live) return;
        try {
          const result = await chatsApi.teamsDirectory(store, address);
          if (live) setDirectory({ address, store, result });
        } catch {
          if (live) setDirectory({ address, store, result: null });
        }
      });
    }, 300);
    return () => { live = false; clearTimeout(timer); };
  }, [address, store]);

  // One colleague on the To: line whose one-to-one chat is here already: open it, as Teams does. Asked again whenever the
  // line changes, and an answer for a line that has changed since is dropped.
  const onlyOne = picked.length === 1 && !pickedOutside[picked[0]!.toLowerCase()] ? picked[0]! : null;
  useEffect(() => {
    if (!onlyOne || !onOpen) return;
    let live = true;
    chatsApi.teamsDirect(store, onlyOne).then((r) => { if (live && r.threadId) onOpen(r.threadId); }, () => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- by who is on the line, not by the parent's callback
  }, [onlyOne, store]);

  const checked = directory?.address === address && directory.store === store ? directory : null;
  const alreadyPicked = address !== null && picked.some((a) => a.toLowerCase() === address);
  const canTakeAddress = !!checked;
  const outside = checked?.result?.inDirectory === false;
  const shown = address
    ? canTakeAddress && !alreadyPicked ? [{ address, name: checked?.result?.name || address }] : []
    : offer(colleagues ?? [], typed, picked);

  /** Somebody offered, taken. The box empties so the next name can be typed straight away. */
  const take = (address: string) => {
    if (!picked.some((x) => x.toLowerCase() === address.toLowerCase())) setPicked((p) => [...p, address]);
    const name = shown.find((c) => c.address === address)?.name;
    if (name) setPickedNames((p) => ({ ...p, [address.toLowerCase()]: name }));
    setPickedOutside((p) => ({ ...p, [address.toLowerCase()]: outside }));
    setTyped(''); setAt(-1); setSaid(null);
  };

  /** What Enter does when the arrow keys are on nobody: take what was typed, if it is an address. */
  const takeTyped = () => {
    const a = typed.trim();
    if (!a) return;
    if (!looksLikeAddress(a)) {
      setSaid(shown.length > 0 ? 'Pick one of the people below, or type a whole address.' : `Nobody here answers to “${a}”. Type their whole work address to add them anyway.`);
      return;
    }
    if (canTakeAddress && !alreadyPicked) take(a.toLowerCase());
  };

  const editor = useMessageEditor({ text, setText, busy, onSend: () => { if (ready) void start(); } });
  const start = async () => {
    if (busy) return;
    setBusy(true); setSaid(null); setNeedConsent(false); setHandover(null);
    try {
      if (picked.some((a) => pickedOutside[a.toLowerCase()])) {
        const tab = openCallWindow();
        tab.go(teamsChatLink(picked, '', text.trim()));
        setHandover('Opened in Teams with everyone in it. Press send there; the chat comes into Cobrain once it starts.');
        return;
      }
      const made = await chatsApi.teamsStartGroup(store, { topic: '', addresses: picked, ...editor.message() });
      if (made.threadId && made.sent && onOpen) onOpen(made.threadId); else onMade(made);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setNeedConsent(true);
      else setSaid(messageOf(e));
    } finally { setBusy(false); }
  };

  const nameOfAddress = (a: string) => pickedNames[a.toLowerCase()] ?? colleagues?.find((c) => c.address.toLowerCase() === a.toLowerCase())?.name ?? a;
  const ready = picked.length > 0 && (picked.some((a) => pickedOutside[a.toLowerCase()]) || text.trim().length > 0);

  return (
    <div className="ch-new-group">
      <div className="ch-people-h"><b>New chat</b><ActionButton reading type="button" className="dotted ch-people-x" onClick={onClose}>Close</ActionButton></div>

      {/* ONE BOX THAT DOES BOTH (B1480). Mike: "you type and then name comes up from the db just like
          in teams ... you add into the chat by typing the name or pasting the external email". What was
          here was a wall of forty name buttons to hunt through AND a separate address field beside it,
          which is neither of the two things he described. */}
      <label className="ch-field ch-to"><span>To:</span>
        <span className="ch-pick">
          {picked.map((a) => (
            <span key={a} className="ch-taken">
              {nameOfAddress(a)}
              {pickedOutside[a.toLowerCase()] ? <span className="ch-tag out">{EXTERNAL_WORD}<Warn /></span> : null}
              <ActionButton type="button" onClick={() => toggle(a)} aria-label={`Take ${nameOfAddress(a)} out`}>&times;</ActionButton>
            </span>
          ))}
          <ActionInput className="ch-pick-in" value={typed} autoFocus
            placeholder={picked.length === 0 ? 'Type a name, or paste an address' : 'Anybody else?'}
            role="combobox" aria-expanded={shown.length > 0 || !!address} aria-autocomplete="list" aria-controls="ch-offer"
            /* Typing puts the highlight on the FIRST match, as Teams does, so "shane" then Enter takes
               Shane without an arrow key. An empty box highlights nobody: Enter there must not add
               whoever happens to be at the top of his list. */
            onChange={(e) => { const v = e.target.value; setTyped(v); setAt(v.trim() ? 0 : -1); setSaid(null); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => moveHighlight(i, e.key === 'ArrowDown' ? 1 : -1, shown.length)); return; }
              if (e.key === 'Enter') { e.preventDefault(); const one = shown[at]; if (one) take(one.address); else takeTyped(); return; }
              if (e.key === 'Escape') { setTyped(''); setAt(-1); return; }
              // Backspace on an empty box takes the last one back out, as every chip field does.
              if (e.key === 'Backspace' && typed === '' && picked.length > 0) { e.preventDefault(); setPicked((p) => p.slice(0, -1)); }
            }} />
        </span>
      </label>

      <div className="ch-offer" id="ch-offer" role="listbox">
        {colleagues === null && !address ? <p className="note">Reading who you talk to…</p>
          : shown.length > 0 ? shown.map((c, i) => (
            <ActionButton key={c.address} type="button" role="option" aria-selected={i === at}
              className={i === at ? 'ch-opt on' : 'ch-opt'}
              onMouseEnter={() => setAt(i)} onClick={() => take(c.address)}>
              {outside ? <>
                <span className="ch-opt-who"><b>{c.address}</b></span>{' '}
                <span className="ch-tag out">{EXTERNAL_WORD}<Warn /></span>
              </> : <>
                <span className="ch-av small" aria-hidden="true">{initials(c.name)}</span>
                <span className="ch-opt-who"><b>{c.name}</b>{' '}<span className="note">{c.address}</span>
                  {address && checked?.result === null ? <span className="note">Could not check that address just now.</span> : null}
                </span>
              </>}
            </ActionButton>
          ))
          : address
            ? <ActionButton type="button" role="option" aria-selected={false} disabled className="ch-opt">
                <span className="ch-av g small" aria-hidden="true">+</span>
                <span className="ch-opt-who"><b>{address}</b><span className="note">{alreadyPicked ? 'Already picked.' : 'Checking that address…'}</span></span>
              </ActionButton>
            : typed.trim()
              ? <p className="note">Nobody answers to that. Type their whole work address to add them anyway.</p>
              : <p className="note">Nobody here yet.</p>}
      </div>

      {/* Teams' own words for an empty new conversation. A chat nobody has spoken in is shown to nobody, here or in
          Teams, so nothing is made until the first message is sent from the box below. */}
      <p className="note ch-pad">You're starting a new conversation. Type your first message below.</p>

      {handover ? <p className="note" role="status">{handover}</p> : null}
      {said ? <p className="note ch-error" role="alert">{said}</p> : null}
      {/* BUGS.md M54: this sat at the top of the panel, above the name, while Start is at the foot; on 12 Oct three
          presses of Start answered it and the person saw nothing happen. It is said where the press is, as `said` is. */}
      {needConsent ? (
        <p className="note ch-pad" role="alert">
          Cobrain needs your permission to start a chat in Teams.{' '}
          <ActionLink href="/api/teams-consent/start">Give it once</ActionLink>, then press Send again.
        </p>
      ) : null}
      {/* The ordinary message box, as in every conversation: one button on the row sends (Mike, 21 Sep). */}
      <div className="ch-comp">
        {editor.bar}
        <div className="ch-compose-row">
          {editor.field}
          {editor.controls}
          <ActionButton type="button" className="btn small" disabled={busy || !ready} onClick={() => void start()}>
            {busy ? 'Sending…' : 'Send'}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

type Calling = { target: string; video: boolean } | null;

function ChatPeople({ store, threadId, openInTeams, onClose, onCall, calling, roster, error, read }: {
  roster: ChatRoster | null; error: string | null; read: () => void;
  store: string | null; threadId: string; openInTeams: string | null; onClose: () => void;
  /** Audio call or Video call through Cobrain's calling bot (BUGS.md M3): one person by address, or the whole room. */
  onCall: (to: string | undefined, video: boolean) => void; calling: Calling;
}) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [needConsent, setNeedConsent] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [adding, setAdding] = useState('');
  const [history, setHistory] = useState<HistoryChoice>('none');
  const failed = (e: unknown) => {
    if (e instanceof ApiError && e.status === 409) { setNeedConsent(true); setSaid(null); return; }
    setSaid(messageOf(e));
  };
  const add = async () => {
    const who = adding.trim().toLowerCase();
    if (!who || busy) return;
    setBusy(true); setSaid(null); setNeedConsent(false);
    try { await chatsApi.teamsAddMember(store, threadId, who, history); setAdding(''); read(); }
    catch (e) { failed(e); } finally { setBusy(false); }
  };
  const remove = async (membershipId: string) => {
    setBusy(true); setSaid(null); setNeedConsent(false); setConfirming(null);
    try { await chatsApi.teamsRemoveMember(store, threadId, membershipId); read(); }
    catch (e) { failed(e); } finally { setBusy(false); }
  };
  const outside = roster ? roster.members.filter((m) => m.external).length : 0;
  return (
    <div className="ch-people" role="group" aria-label="Who is in this conversation">
      <div className="ch-people-h">
        <b>{roster?.topic ?? 'In this conversation'}</b>
        <ActionButton reading type="button" className="dotted ch-people-x" onClick={onClose}>Close</ActionButton>
      </div>
      {error ? <p className="note ch-pad" role="alert">{error}</p> : null}
      {!roster && !error ? <p className="note ch-pad">Asking Teams who is in it…</p> : null}
      {roster ? (
        <>
          <p className="ch-people-sub">
            {roster.members.length.toLocaleString()} {roster.members.length === 1 ? 'person' : 'people'}
            {outside > 0 ? ` · ${outside.toLocaleString()} outside iGame Media` : ' · all colleagues'}
            {roster.chatType === 'oneOnOne' ? ' · a one-to-one chat, so nobody can be added to it' : null}
            {roster.chatType === 'meeting' ? ' · a meeting chat, so its people follow the meeting' : null}
          </p>
          <ul className="ch-people-l">
            {roster.members.map((m, i) => (
              <li key={`${m.address ?? 'x'}:${i}`} className={m.external ? 'out' : undefined}>
                <span className={m.external ? 'ch-av g small' : 'ch-av small'} aria-hidden="true">{m.name ? initials(m.name) : '·'}</span>
                <span className="ch-people-who">
                  {/* Teams gives no name for a guest it never resolved. It gives an identifier, and an
                      identifier is not a name: the list learned that four times over. */}
                  <b>{m.name ?? 'Someone Teams has not named'}</b>
                  {m.address ? <span className="ch-people-at">{m.address}</span> : null}
                </span>
                  {m.owner ? <span className="ch-tag">Owner</span> : null}
                {m.external ? <span className="ch-tag out">{EXTERNAL_WORD}<Warn /></span> : null}
                {/* Ringing this one person (B1223), through Cobrain's calling bot so the call is recorded and goes
                    into Meetings (BUGS.md M3; Mike, 5 Oct 2026: "when you select to call a person from chats, the
                    objective is that it records and registers the call in meetings as cobrain"). Colleagues only:
                    nobody outside iGame is rung ad hoc (his answer of 5 Oct). Absent for the reader themselves and
                    for anybody Teams named without an address. */}
                {m.call.audio && m.address && !m.external ? (
                  <span className="ch-one-call">
                    <ActionButton type="button" disabled={calling !== null} title={`Audio call, ${m.name ?? 'them'}`} aria-label={calling?.target === m.address && !calling.video ? 'Calling…' : `Audio call, ${m.name ?? 'them'}`} onClick={() => onCall(m.address!, false)}>{calling?.target === m.address && !calling.video ? 'Calling…' : 'Audio call'}</ActionButton>
                    <ActionButton type="button" disabled={calling !== null} title={`Video call, ${m.name ?? 'them'}`} aria-label={calling?.target === m.address && calling.video ? 'Calling…' : `Video call, ${m.name ?? 'them'}`} onClick={() => onCall(m.address!, true)}>{calling?.target === m.address && calling.video ? 'Calling…' : 'Video call'}</ActionButton>
                  </span>
                ) : null}
                {/* Taking somebody out of a room is not undone by pressing again, so it asks once. Only on a
                    group chat: Teams fixes a one-to-one chat's roster and would refuse. */}
                {roster.rosterCanChange && !m.you && m.membershipId ? (
                  confirming === m.membershipId
                    ? <span className="ch-people-go">
                        <ActionButton type="button" className="ch-people-x" disabled={busy} onClick={() => setConfirming(null)}>Keep</ActionButton>
                        <ActionButton type="button" className="ch-rm on" disabled={busy} onClick={() => void remove(m.membershipId!)}>Remove {m.name ?? 'them'}</ActionButton>
                      </span>
                    : <ActionButton type="button" className="ch-rm" disabled={busy} aria-label={`Remove ${m.name ?? 'this person'}`} onClick={() => setConfirming(m.membershipId)}>Remove</ActionButton>
                ) : null}
              </li>
            ))}
          </ul>
          {said ? <p className="note ch-people-f" role="alert">{said}</p> : null}
          {needConsent ? (
            <p className="ch-people-f">Cobrain needs your permission once more before it can change who is in a Teams chat as you.{' '}
              <ActionLink className="ch-open" href={`/api/teams-consent/start?next=${encodeURIComponent(`/chats/${threadId}`)}`}>Allow it</ActionLink></p>
          ) : null}
          {/* Adding somebody. The history choice is asked every time and has no default that shares anything:
              it decides how much of a conversation they were not in a new person may read, and Teams asks it
              every time for the same reason. The API refuses an add that does not carry one. */}
          {roster.rosterCanChange ? (
            <div className="ch-people-add">
              <label className="ch-people-lab" htmlFor={`add-${threadId}`}>Add someone</label>
              <div className="ch-people-row">
                <ActionInput id={`add-${threadId}`} className="ch-find" type="email" placeholder="their work address" value={adding}
                  disabled={busy} onChange={(e) => setAdding(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add(); } }} />
                <ActionButton type="button" className="btn small" disabled={busy || !adding.trim()} onClick={() => void add()}>{busy ? 'Working…' : 'Add'}</ActionButton>
              </div>
              <div className="ch-people-hist" role="radiogroup" aria-label="How much of this conversation they may read">
                {([['none', 'Nothing said so far'], ['month', 'The last month'], ['all', 'The whole conversation']] as const).map(([k, w]) => (
                  <ActionButton key={k} type="button" className={history === k ? 'ch-hist on' : 'ch-hist'} role="radio" aria-checked={history === k}
                    disabled={busy} onClick={() => setHistory(k)}>{w}</ActionButton>
                ))}
              </div>
            </div>
          ) : null}
          {/* Calling the people who are in the room NOW, which is the reason this panel knows them: the list's
              own "people" are everyone who has ever been on a message here, and ringing somebody who left the
              group last year because their name is on an old message would be worse than having no button. The
              server reads the roster again when it is pressed. Not offered with anybody outside iGame in the room:
              "There would never be a situation where I would want to ahoc call a group with an external client as
              these would always be calendared" (Mike, 5 Oct 2026). */}
          {roster.call.audio && outside === 0 ? (
            <p className="ch-people-call">
              <ActionButton type="button" className="ch-call" disabled={calling !== null} onClick={() => onCall(undefined, false)}>{calling?.target === 'room' && !calling.video ? 'Calling…' : 'Audio call'}</ActionButton>
              <ActionButton type="button" className="ch-call" disabled={calling !== null} onClick={() => onCall(undefined, true)}>{calling?.target === 'room' && calling.video ? 'Calling…' : 'Video call'}</ActionButton>
            </p>
          ) : null}
          {/* The thing itself, where the roster can actually be changed. Cobrain has read-only access to a
              chat's members: adding and removing needs ChatMember.ReadWrite, which nobody has granted. */}
          {roster.rosterCanChange && openInTeams
            ? <p className="ch-people-f"><a href={openInTeams} target="_blank" rel="noreferrer">Add or remove people in Teams ↗</a></p>
            : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * A message the store holds no words for: the picture itself where there is one, and an honest line
 * where there is not (B1451).
 *
 * Until this existed every one of them read "Attachment or reaction", which on Mike's store is 7,176
 * messages -- one in ten -- saying nothing about what they are. Of thirty asked about, eleven were
 * pictures and thirteen were files somebody shared.
 *
 * `null` while the answer is still on its way, and a picture that will not load falls back to the same
 * line rather than leaving a broken frame in the conversation.
 */
function Shown({ store, threadId, extra }: { store: string | null; threadId: string; extra: MessageExtra | null }) {
  const [broke, setBroke] = useState(false);
  if (!extra || extra.kind === 'nothing') return <div className="ch-m-body note">Attachment or reaction</div>;
  if (extra.kind === 'picture' && !broke) {
    return (
      <div className="ch-m-body">
        {extra.text ? <div className="ch-m-cap">{extra.text}</div> : null}
        {Array.from({ length: Math.min(extra.pictures, 8) }, (_, i) => (
          <img key={i} className="ch-m-pic" loading="lazy" alt={extra.text ?? 'A picture sent in this conversation'}
            src={chatsApi.teamsPictureUrl(store, threadId, extra.signalId, i)} onError={() => setBroke(true)} />
        ))}
      </div>
    );
  }
  if (extra.kind === 'file') {
    return <div className="ch-m-body note">📎 {extra.name ?? 'A file, shared in Teams'}</div>;
  }
  // Words the ingest dropped and Graph still has: shown as what they are, not as an attachment.
  if (extra.kind === 'text' && extra.text) return <div className="ch-m-body">{extra.text}</div>;
  return <div className="ch-m-body note">{extra.kind === 'card' ? 'A card from Teams' : 'Attachment or reaction'}</div>;
}

/** One Teams conversation, in this page's look. */
/** The companies on a conversation, as their lines (pipeline RC6): the name at once, the line when it is read. */
function ChatLines({ store, companies }: { store: string | null; companies: Array<{ id: string; name: string }> }) {
  const lines = useCompanyLines(store, companies.map((c) => c.id));
  return <div className="ch-lines">{companies.map((c) => <LineOrName key={c.id} id={c.id} name={c.name} line={lines[c.id.toLowerCase()]} />)}</div>;
}

/** The head's menu keeps the existing recorded-call path. Opening it is only a read. */
function TeamsCallMenu({ calling, onCall }: { calling: Calling; onCall: (video: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  useEffect(() => {
    if (!open) return;
    items.current[0]?.focus();
    const outside = (e: PointerEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);
  return <div className="ch-call-menu" ref={root}>
    <ActionButton reading ref={trigger} type="button" className="ch-new" aria-label="Video call" title="Video call"
      aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(v => !v)}
      onKeyDown={e => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); } }}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="6" width="12" height="12" rx="2" /><path d="m15 10 6-4v12l-6-4" /></svg>
    </ActionButton>
    {open ? <div className="ch-call-options" role="menu" aria-label="Video call" onKeyDown={e => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const current = items.current.indexOf(document.activeElement as HTMLButtonElement);
        items.current[(current + (e.key === 'ArrowDown' ? 1 : -1) + 2) % 2]?.focus();
      }
      if (e.key === 'Tab') setOpen(false);
    }}>
      {[true, false].map((video, i) => <ActionButton key={String(video)} ref={el => { items.current[i] = el; }}
        type="button" role="menuitem" tabIndex={-1} disabled={calling !== null} onClick={() => onCall(video)}>
        {calling?.target === 'header' && calling.video === video ? 'Calling…' : video ? 'Video call' : 'Audio call'}
      </ActionButton>)}
    </div> : null}
  </div>;
}

function MarkedWords({ text, query }: { text: string; query: string }) {
  // Literal words, including punctuation, never interpreted as a regular expression or HTML.
  const lower = text.toLocaleLowerCase();
  const words = query.toLocaleLowerCase().split(/\s+/).filter(Boolean);
  const parts: ReactNode[] = [];
  let start = 0;
  while (start < text.length) {
    const hits = words.map(word => ({ at: lower.indexOf(word, start), word })).filter(hit => hit.at >= 0)
      .sort((a, b) => a.at - b.at || b.word.length - a.word.length);
    const hit = hits[0];
    if (!hit) { parts.push(text.slice(start)); break; }
    if (hit.at > start) parts.push(text.slice(start, hit.at));
    parts.push(<mark key={hit.at}>{text.slice(hit.at, hit.at + hit.word.length)}</mark>);
    start = hit.at + hit.word.length;
  }
  return <>{parts}</>;
}

function FindInChat({ store, threadId, view, onPick, onClose }: {
  store: string | null; threadId: string; view: TeamsThreadView | null; onPick: (id: string) => void; onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState<{ query: string; view: TeamsFindView } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const words = query.trim();
  useEffect(() => {
    let live = true;
    setAnswer(null); setError(null);
    if (words.length < 2) return;
    const timer = window.setTimeout(() => {
      chatsApi.teamsFind(store, threadId, words).then(result => {
        if (live) setAnswer({ query: words, view: result });
      }).catch((e: unknown) => { if (live) setError(messageOf(e)); });
    }, 250);
    return () => { live = false; window.clearTimeout(timer); };
  }, [store, threadId, words]);
  const matches = answer?.query === words ? [...answer.view.matches].sort((a, b) => b.sentAt.localeCompare(a.sentAt)) : null;
  return <div className="ch-people ch-thread-find" role="region" aria-label="Find in chat"
    onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
    <ActionInput reading autoFocus className="ch-find" aria-label="Find in chat" placeholder="Find in chat"
      value={query} onChange={e => setQuery(e.target.value)} />
    {error ? <p className="note" role="alert">{error}</p> : null}
    {matches?.length === 0 ? <p className="note">Nothing found.</p> : null}
    {matches ? <ul className="ch-find-matches">{matches.map(m => {
      const loaded = view?.messages.some(message => message.id === m.id);
      const content = <><span className="ch-find-who">{m.sender}<time>{whenWords(m.sentAt)}</time></span>
        <span><MarkedWords text={m.snippet} query={words} /></span></>;
      return <li key={m.id}>
        {loaded ? <ActionButton reading type="button" className="ch-find-match" onClick={() => onPick(m.id)}>{content}</ActionButton>
          : <div className="ch-find-match">{content}</div>}
        {!loaded ? <p className="note ch-find-earlier">{view?.openInTeams
          ? <a href={view.openInTeams} target="_blank" rel="noreferrer">Earlier messages are in Teams.</a>
          : 'Earlier messages are in Teams.'}</p> : null}
      </li>;
    })}</ul> : null}
  </div>;
}

function TeamsThread({ store, row, onBack, myName, onSent }: { store: string | null; row: ChatRow; onBack: () => void; myName: string | null; onSent: () => void }) {
  // What was sent from here in this sitting. Teams has it the moment the API answers, but Cobrain's own
  // copy arrives only when the connector next reads the conversation, so without this a person presses
  // Send and the thread shows nothing at all. It is drawn as "sending into Teams" rather than as an
  // ordinary message, because it is Cobrain's word for it and not yet the store's record of it.
  const [justSent, setJustSent] = useState<{ at: string; body: string }[]>([]);
  const [view, setView] = useState<TeamsThreadView | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Replaces what is on screen without emptying it first, which is what lets this be called on a beat:
  // a loader that blanks its own state before fetching flashes the whole conversation to "Opening…"
  // every ten seconds, and scrolls the reader off whatever they were reading (live.ts, B1041).
  const quiet = useCallback(() => chatsApi.teamsThread(store, row.id).then(
    (v) => { setView(v); setError(null); },
    (e: unknown) => { setError(messageOf(e)); },
  ), [store, row.id]);
  // Opening a DIFFERENT conversation does empty it: nobody may see one person's messages under another
  // person's name, not even for the 471 ms the read takes.
  useEffect(() => {
    let live = true;
    setView(null); setError(null); setJustSent([]);
    chatsApi.teamsThread(store, row.id).then((v) => { if (live) setView(v); }).catch((e: unknown) => { if (live) setError(messageOf(e)); });
    return () => { live = false; };
  }, [store, row.id]);
  // And from then on it keeps up by itself: see THREAD_BEAT_MS.
  useLiveRefresh(quiet, THREAD_BEAT_MS);
  // WHAT THE MESSAGES SHOWING NOTHING ACTUALLY ARE (B1451). Mike, 2026-09-21: "I cannot see the photo."
  // One message in ten on his store is drawn as "Attachment or reaction", because the ingest put the
  // html body through a text converter and the <img> went with the tags; Graph still has the original.
  // Asked for when a conversation is opened and when a new message lands -- NOT on the beat above, which
  // would be six Graph calls a minute at a conversation nobody is typing in.
  const [extras, setExtras] = useState<Record<string, MessageExtra>>({});
  const blanks = (view?.messages ?? []).filter((m) => !(m.body ?? '').trim()).length;
  const newestId = view ? view.messages[view.messages.length - 1]?.id ?? '' : null;
  useEffect(() => {
    if (!view || blanks === 0) return;
    let live = true;
    chatsApi.teamsExtras(store, row.id).then((a) => {
      if (!live) return;
      const by: Record<string, MessageExtra> = {};
      for (const e of a.extras) by[e.signalId] = e;
      setExtras(by);
    }, () => { /* the thread draws exactly what it drew before this existed */ });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- by the conversation and its newest message,
    // never by `view`, which is a new object on every beat (see the scroll note above and live.ts).
  }, [store, row.id, newestId, blanks > 0]);
  useEffect(() => { setExtras({}); }, [store, row.id]);
  const [reactions, setReactions] = useState<Record<string, MessageReaction[]>>({});
  const reactionRead = useRef(0);
  useEffect(() => {
    if (!view) return;
    let live = true;
    chatsApi.teamsReactions(store, row.id).then(answer => {
      if (!live) return;
      reactionRead.current++;
      setReactions(Object.fromEntries(answer.reactions.map(r => [r.signalId, r.list])));
    }, () => { /* Like extras, an unavailable read adds no error line. */ });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- exactly the extras dependencies, never the live view object.
  }, [store, row.id, newestId, blanks > 0]);
  useEffect(() => { setReactions({}); }, [store, row.id]);
  // A message sent from here stands as an ordinary message of his until COBRAIN'S OWN copy of it arrives,
  // and then it simply is one. The rule is in pending.ts, where it can be proved: sending as Mike cannot
  // be driven on a throwaway app, because the only way to make one send is to let it reach his real Teams.
  const pending = stillSending(justSent, view?.messages ?? []);
  // A chat opens at its newest message. Without this the pane sits at the top of the fetched window, so a
  // person lands in the middle of a 375-message conversation and has to drag down to see what just arrived.
  const msgs = useRef<HTMLDivElement | null>(null);
  // WHAT MAY MOVE THIS PANE, now that it re-reads on a beat (B1410): a different conversation, a message
  // arriving at the end of this one, or one of his own going out. Not every re-read. `view` is a new
  // object each time the beat answers, so the old `[view]` here would have dragged him to the bottom six
  // times a minute -- out of whatever history he had scrolled up to read, and with no way to stay there.
  const newest = view?.messages[view.messages.length - 1]?.id ?? '';
  const foot = `${row.id}|${newest}|${pending.length}`;
  const footWas = useRef<string | null>(null);
  useEffect(() => {
    const el = msgs.current;
    if (!view || !el) return;
    if (footWas.current === foot) return;
    const opening = footWas.current === null || !footWas.current.startsWith(`${row.id}|`);
    // Somebody reading back through a conversation stays where they are. A new message announces itself
    // by being in the list; it does not get to move the page under the person reading.
    const atFoot = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    footWas.current = foot;
    if (!opening && !atFoot) return;
    el.scrollTop = el.scrollHeight;
    // scrollTop moves an element that scrolls and is ignored by one that does not, so this line alone was
    // a fix at one width: below 760px the pane grew to its content and every conversation opened at the
    // oldest message of its window. The stylesheet keeps the pane a scroller at both widths now, and this
    // is the belt for it -- whatever the height rules do later, the newest message is what you land on.
    if (el.scrollHeight <= el.clientHeight + 2) el.lastElementChild?.scrollIntoView({ block: 'end' });
  }, [view, foot, row.id]);
  const [topic, setTopic] = useState<string | null>(null);
  const name = nameOf(topic === null ? row : { ...row, title: topic }, myName);
  const [roster, setRoster] = useState<ChatRoster | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);
  // Once on opening, shared with People. Only a membership change asks again; never the live beat.
  const rosterRead = useRef(0);
  const readRoster = useCallback(() => {
    const read = ++rosterRead.current;
    chatsApi.teamsPeople(store, row.id).then(r => {
      if (read === rosterRead.current) { setRoster(r); setRosterError(null); }
    }).catch((e: unknown) => { if (read === rosterRead.current) setRosterError(messageOf(e)); });
  }, [store, row.id]);
  useEffect(() => { readRoster(); return () => { rosterRead.current++; }; }, [readRoster]);
  const [find, setFind] = useState(false);
  const findButton = useRef<HTMLButtonElement>(null);
  const messageNodes = useRef(new Map<string, HTMLDivElement>());
  const [highlight, setHighlight] = useState<string | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (highlightTimer.current) clearTimeout(highlightTimer.current); }, []);
  const pickMatch = (id: string) => {
    messageNodes.current.get(id)?.scrollIntoView({ block: 'center' });
    setHighlight(id);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlight(null), 2000);
  };
  const [naming, setNaming] = useState(false);
  const [draftTopic, setDraftTopic] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renameConsent, setRenameConsent] = useState(false);
  const saveButton = useRef<HTMLButtonElement>(null);
  const rename = async () => {
    if (renaming || !draftTopic.trim()) return;
    const next = draftTopic.trim();
    setRenaming(true); setRenameError(null); setRenameConsent(false);
    try {
      await chatsApi.teamsRename(store, row.id, next);
      setTopic(next); setRoster(r => r ? { ...r, topic: next } : r); setNaming(false);
    } catch (e) {
      setRenameError(messageOf(e));
      setRenameConsent(e instanceof ApiError && e.status === 409);
    } finally { setRenaming(false); }
  };
  const [people, setPeople] = useState(false);
  const [reacting, setReacting] = useState<string | null>(null);
  const [calling, setCalling] = useState<Calling>(null);
  const [callLine, setCallLine] = useState<{ said: string } | null>(null);
  const [reactFailed, setReactFailed] = useState<string | null>(null);
  const react = async (signalId: string, teamsMessageId: string, which: string | { emoji: string }, on = true) => {
    if (reacting) return;
    const emoji = typeof which === 'string' ? REACTIONS.find(r => r.name === which)!.glyph : which.emoji;
    const before = reactions[signalId] ?? [];
    const read = reactionRead.current;
    const ownName = myName ?? roster?.members.find(m => m.you)?.name;
    setReacting(teamsMessageId); setReactFailed(null);
    setReactions(all => {
      const list = all[signalId] ?? [];
      const held = list.find(r => r.emoji === emoji);
      if (held?.mine === on) return all;
      const changed = { emoji, count: (held?.count ?? 0) + (on ? 1 : -1), mine: on,
        names: on ? [...(held?.names ?? []), ...(ownName ? [ownName] : [])]
          : (held?.names ?? []).filter(name => name !== ownName) };
      return { ...all, [signalId]: (held ? list.map(r => r.emoji === emoji ? changed : r) : [...list, changed]).filter(r => r.count > 0) };
    });
    try {
      await chatsApi.teamsReact(store, row.id, teamsMessageId, which, on);
    } catch (e) {
      // A later read is authoritative, including when a pending write fails afterwards.
      if (read === reactionRead.current) setReactions(all => ({ ...all, [signalId]: before }));
      setReactFailed(e instanceof ApiError && e.status === 409
        ? 'Cobrain needs your permission once before it can react as you, below.'
        : messageOf(e));
    } finally { setReacting(null); }
  };
  // Both choices book a recorded meeting (REQ-2026-10-15-41). Audio rings the caller too, without opening
  // anything. Video reserves a tab during the press and opens the meeting in Teams when the bot rings.
  const call = async (to: string | undefined, video: boolean, target = 'header') => {
    if (calling) return;
    setCalling({ target: to ?? target, video }); setCallLine(null);
    try {
      if (!video) {
        const o = await placeChatCall(store, row.id, { ...(to ? { to } : {}), video: false });
        setCallLine(chatCallSaid(o));
        return;
      }
      const tab = openCallWindow();
      try {
        const o = await placeChatCall(store, row.id, { ...(to ? { to } : {}), video: true });
        if (o.ok && o.result.rung) tab.go(o.result.joinUrl);
        else tab.close();
        setCallLine(chatCallSaid(o));
      } catch (e) {
        tab.close();
        throw e;
      }
    } catch (e) {
      setCallLine({ said: messageOf(e) });
    } finally { setCalling(null); }
  };
  return (
    <div className={row.hasExternal ? 'ch-thread out' : 'ch-thread'}>
      <div className={row.hasExternal ? 'ch-th out' : 'ch-th'}>
        <ActionButton reading type="button" className="dotted ch-back" onClick={onBack}>← Chats</ActionButton>
        <div className="ch-head-name">
          {naming ? <>
            <ActionInput autoFocus className="ch-find" aria-label="Name group chat" value={draftTopic} disabled={renaming}
              onChange={e => setDraftTopic(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); saveButton.current?.click(); }
                if (e.key === 'Escape') { setNaming(false); setRenameError(null); setRenameConsent(false); }
              }} />
            <ActionButton ref={saveButton} type="button" className="ch-call-b" disabled={renaming || !draftTopic.trim()} onClick={() => void rename()}>Save</ActionButton>
          </> : <>
            <b>{name}</b>
            {roster?.chatType === 'group' ? <ActionButton type="button" className="ch-new" aria-label="Name group chat" title="Name group chat"
              onClick={() => { setDraftTopic(topic ?? row.title ?? ''); setNaming(true); setRenameError(null); setRenameConsent(false); }}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 5 5 5M4 20l5-1L20 8a2 2 0 0 0-5-5L4 14z" /></svg>
            </ActionButton> : null}
          </>}
        </div>
        <div className="ch-head-actions">
          {row.hasExternal ? null : <TeamsCallMenu calling={calling} onCall={video => void call(undefined, video)} />}
          <ActionButton reading type="button" className="ch-new ch-head-people" aria-label={roster ? `People, ${roster.members.length}` : 'People'}
            title="People" aria-expanded={people} onClick={() => { setPeople(v => !v); setFind(false); }}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="7" r="3" /><path d="M3 20v-2a6 6 0 0 1 12 0v2M16 4a3 3 0 0 1 0 6m2 3a5 5 0 0 1 3 5v2" /></svg>
            {roster ? <span>{roster.members.length}</span> : null}
          </ActionButton>
          <ActionButton reading ref={findButton} type="button" className="ch-new" aria-label="Find in chat" title="Find in chat"
            aria-expanded={find} onClick={() => { setFind(v => !v); setPeople(false); }}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10" cy="10" r="6" /><path d="m15 15 6 6" /></svg>
          </ActionButton>
        </div>
      </div>
      {renameError ? <p className="note ch-pad" role="alert">{renameError}{renameConsent ? <>{' '}
        <ActionLink href={`/api/teams-consent/start?next=${encodeURIComponent(`/chats/${row.id}`)}`}>Allow it</ActionLink></> : null}</p> : null}
      {find ? <FindInChat store={store} threadId={row.id} view={view} onPick={pickMatch}
        onClose={() => { setFind(false); findButton.current?.focus(); }} /> : null}
      {callLine ? (
        <p className="note ch-pad" role="status">
          {callLine.said}
        </p>
      ) : null}
      {row.hasExternal ? (
        <p className="ch-band"><b>{EXTERNAL_WORD}<Warn /></b> {outsiders(row.people)}</p>
      ) : null}
      {/* The line of each company on the conversation (U353), its name a link to the record (X30). */}
      {row.hasExternal && row.companies && row.companies.length > 0 ? <ChatLines store={store} companies={row.companies.slice(0, 2)} /> : null}
      {people ? <ChatPeople roster={roster} error={rosterError} read={readRoster} store={store} threadId={row.id} openInTeams={view?.openInTeams ?? null} onClose={() => setPeople(false)} onCall={(to, video) => void call(to, video, 'room')} calling={calling} /> : null}
      {reactFailed ? <p className="note ch-pad" role="alert">{reactFailed}</p> : null}
      {error ? <p className="note ch-pad" role="alert">{error}</p> : null}
      {!view && !error ? <p className="note ch-pad">Opening…</p> : null}
      {view ? (
        <div className="ch-msgs" ref={msgs}>
          {view.more ? <p className="note ch-older">Earlier messages are in Teams.</p> : null}
          {view.messages.map((m) => (
            <div key={m.id} ref={el => { if (el) messageNodes.current.set(m.id, el); else messageNodes.current.delete(m.id); }} className={`ch-m${highlight === m.id ? ' ch-found' : ''}${m.mine ? ' mine' : ''}${m.external ? ' out' : ''}`}>
              {/* The fourth place a person's name is drawn, and the last one the id filter did not reach. */}
              {m.mine ? null : <span className="ch-av small" aria-hidden="true">{named(m.sender) ? initials(m.sender!) : '·'}</span>}
              <div className="ch-b">
                {m.mine ? null : <div className="ch-m-who">{named(m.sender) ? m.sender : 'Someone'}</div>}
                {m.body
                  ? <div className="ch-m-body">{m.body}</div>
                  : <Shown store={store} threadId={row.id} extra={extras[m.id] ?? null} />}
                <div className="ch-m-t">
                  {whenWords(m.sentAt)}
                  <Reactions on={!!m.teamsMessageId} busy={reacting !== null}
                    onPick={(which) => { if (m.teamsMessageId) void react(m.id, m.teamsMessageId, which); }} />
                </div>
                {(reactions[m.id]?.length ?? 0) > 0 ? <div className="ch-reaction-chips">
                  {reactions[m.id]!.map(r => {
                    const label = `${r.emoji} ${r.count}: ${r.names.join(', ')}`;
                    return <ActionButton key={r.emoji} type="button" className="ch-reaction-chip" aria-pressed={r.mine}
                      title={label} aria-label={label} disabled={!m.teamsMessageId || reacting !== null}
                      onClick={() => { if (m.teamsMessageId) void react(m.id, m.teamsMessageId, { emoji: r.emoji }, !r.mine); }}>
                      {r.emoji} {r.count}
                    </ActionButton>;
                  })}
                </div> : null}
              </div>
            </div>
          ))}
          {pending.map((m) => (
            <div key={m.at} className="ch-m mine sent">
              <div className="ch-b">
                <div className="ch-m-body">{m.body}</div>
                {/* It IS sent -- Teams had it before this bubble was drawn. The old line here explained
                    Cobrain's own reading schedule to him instead, which is not a thing a person sending a
                    message has any reason to know. */}
                <div className="ch-m-t">{whenWords(m.at)} &middot; Sent</div>
              </div>
            </div>
          ))}
          {view.messages.length === 0 && pending.length === 0 ? <p className="note ch-pad">{row.messages > 0 ? 'These messages are in Teams.' : 'No messages in this conversation.'}</p> : null}
        </div>
      ) : null}
      {/* The thing itself, not a sentence about what Cobrain cannot do yet: this conversation, open in Teams
          where a reply can actually be typed. A page never tells a person what we have not written. */}
      <Composer store={store} threadId={row.id} name={name} external={row.hasExternal}
        onSent={(said) => { setJustSent((all) => [...all, { at: new Date().toISOString(), body: said }]); onSent(); void quiet(); }} />
      {view?.openInTeams
        ? <p className="ch-alsoin"><a href={view.openInTeams} target="_blank" rel="noreferrer">Open in Teams ↗</a></p>
        : null}
    </div>
  );
}

const NARROW = '(max-width: 760px)';
function useNarrow(): boolean {
  return useSyncExternalStore(
    (cb) => { if (typeof window === 'undefined') return () => {}; const m = window.matchMedia(NARROW); m.addEventListener('change', cb); return () => m.removeEventListener('change', cb); },
    () => (typeof window === 'undefined' ? false : window.matchMedia(NARROW).matches),
    () => false,
  );
}

/**
 * The panes' height: what the window has left below their top, less what the page draws after them, so the page itself
 * never scrolls and the conversation's foot (its newest message and the box) is on screen as it opens. BUGS.md M57
 * (Chats brief CT2): a fixed calc(100vh - 210px) left the page 86 px taller than the window on live at 1456x900, so
 * the newest message sat below the fold until Mike scrolled. Null until measured, and in one pane on a phone, where
 * the stylesheet's own rule holds.
 */
function useFitHeight(split: RefObject<HTMLDivElement | null>, narrow: boolean, deps: readonly unknown[]): number | null {
  const [h, setH] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (narrow) { setH(null); return; }
    const fit = () => {
      const el = split.current;
      if (!el) return;
      // Above: where the panes start on the page (a strip above the shell counts). Below: only what is really drawn
      // under them, the padding, borders and margins of the panes' own boxes and anything placed after them. Not the
      // page's height: the shell is at least a window tall, so that would count room the panes are meant to fill.
      const top = el.getBoundingClientRect().top + window.scrollY;
      let below = parseFloat(getComputedStyle(el).marginBottom) || 0;
      for (let node: HTMLElement | null = el; node && node !== document.body; node = node.parentElement) {
        for (let sib = node.nextElementSibling; sib; sib = sib.nextElementSibling) {
          const st = getComputedStyle(sib);
          if (st.position !== 'fixed' && st.position !== 'absolute') below += (sib as HTMLElement).offsetHeight + (parseFloat(st.marginTop) || 0) + (parseFloat(st.marginBottom) || 0);
        }
        const parent = node.parentElement;
        if (parent && parent !== document.body) {
          const ps = getComputedStyle(parent);
          below += (parseFloat(ps.paddingBottom) || 0) + (parseFloat(ps.borderBottomWidth) || 0);
        }
      }
      const next = Math.max(340, Math.floor(window.innerHeight - top - below));
      setH((was) => (was === next ? was : next));
    };
    fit();
    // A strip drawn above the shell after this page (the onboarding prompt, a notice) moves the panes down without
    // resizing the window or the body, which is a window tall whatever it holds. So a change to the page's elements
    // re-measures, at most once in 50 ms (a timer, not an animation frame, which a hidden tab never runs); a height
    // that does not change is not set again.
    let frame = 0;
    const soon = () => { if (!frame) frame = window.setTimeout(() => { frame = 0; fit(); }, 50); };
    const watch = typeof MutationObserver === 'undefined' ? null : new MutationObserver(soon);
    watch?.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', soon);
    return () => { window.removeEventListener('resize', soon); watch?.disconnect(); if (frame) window.clearTimeout(frame); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-measured when what sits above the panes changes
  }, [narrow, ...deps]);
  return h;
}

export function ChatsPage({ store, id, go }: { store: Store; id?: string; go: (path: string, opts?: { replace?: boolean }) => void }) {
  const tenant = store.tenantId;
  const [view, setView] = useState<ChatsView | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [f, setF] = useFilterParams(KEYS);
  const filter = oneOf(f.show, FILTERS, 'all');
  const setFilter = (k: Filter) => setF({ show: k === 'all' ? '' : k });
  const find = f.q;
  /** A conversation's address, with the list's narrowing carried so it is still there on the way back. */
  const at = (path: string) => `${path}${window.location.search}`;
  const [creating, setCreating] = useState(false);
  // A group just made in Teams is not here yet: it arrives by the worker's delta and the card pass,
  // which is seconds to a couple of minutes. Rather than guess at its name, remember which Teams
  // conversations were on the list BEFORE it was made and open the one that was not.
  const [waitingFor, setWaitingFor] = useState<{ made: StartedGroup; knew: Set<string> } | null>(null);
  const teamsIdsNow = () => new Set((view?.chats ?? []).filter((c) => c.source === 'teams').map((c) => c.id));

  // Returns its promise, and passes the signal on. That is what lets the policy hold the next re-read
  // until this one has answered, and end it when he leaves the page (live.ts). A refresh that returns
  // nothing still works and simply gets neither.
  const refresh = useCallback((signal?: AbortSignal) => Promise.allSettled([
    chatsApi.list(tenant, signal).then((v) => { setView(v); setError(null); }).catch((e: unknown) => {
      // An abandoned read is not a fault to show him: he either left the page or a newer read is coming.
      if (signal?.aborted || (e instanceof DOMException && e.name === 'AbortError')) return;
      setError(messageOf(e));
    }),
    messenger.conversations(tenant).then((r) => setMe(r.me)).catch(() => {}),
  ]), [tenant]);
  useEffect(() => { void refresh(); }, [refresh]);
  // While a new group is on its way, ask more often than the page otherwise would: this is the one
  // moment somebody is actually watching for a conversation to appear. It stops the moment it does,
  // and after five minutes, so a group that never arrives does not poll for ever.
  useEffect(() => {
    if (!waitingFor) return;
    const started = Date.now();
    const t = window.setInterval(() => {
      if (Date.now() - started > 5 * 60_000) { window.clearInterval(t); return; }
      void refresh();
    }, 10_000);
    return () => window.clearInterval(t);
  }, [waitingFor, refresh]);
  // It has arrived when a Teams conversation is on the list that was not there when it was made.
  useEffect(() => {
    if (!waitingFor || !view) return;
    const fresh = view.chats.find((c) => c.source === 'teams' && !waitingFor.knew.has(c.id));
    if (!fresh) return;
    setWaitingFor(null);
    go(at(`/chats/${fresh.id}`));
  }, [view, waitingFor]);
  // The Cobrain half arrives on a socket. The TEAMS half does not: nothing pushes a Teams message at this
  // page, so a tab left open showed whatever was true when it was opened (Mike, 2026-09-21). It re-reads
  // when he comes back to it and on a slow beat while he is looking at it, and never while it is hidden.
  useLiveRefresh(refresh, LIVE_BEAT_MS);
  useMessengerSocket(tenant, (e) => { if (e.type === 'message' || e.type === 'conversation' || e.type === 'read' || e.type === 'delete') refresh(); });

  const rows = view?.chats ?? [];
  const selected = id ? rows.find((c) => c.id === id) ?? null : null;   // ids are uuids from one store; source is carried on the row it finds
  // The chip narrows first; By company offers the companies of what the chip leaves, so an option never empties it.
  const chipped = useMemo(() => rows.filter((c) => (
    filter === 'cobrain' ? c.source === 'cobrain' : filter === 'teams' ? c.source === 'teams' : filter === 'external' ? c.hasExternal : true
  )), [rows, filter]);
  const shown = useMemo(() => chipped.filter((c) => {
    if (f.company && !(c.companies ?? []).some((o) => o.id === f.company)) return false;
    return matchesWords(find, nameOf(c, me?.displayName ?? null), c.lastPreview, c.people.map((p) => p.name).join(' '), (c.companies ?? []).map((o) => o.name).join(' '));
  }), [chipped, f.company, find, me]);

  // Read live, and from the same 760px the stylesheet splits the panes at. Measured once at mount, a resize
  // left a person inside a thread with the list stacked above it and no way back.
  const narrow = useNarrow();
  const splitRef = useRef<HTMLDivElement | null>(null);
  const fitHeight = useFitHeight(splitRef, narrow, [view === null, error, f.company, filter]);
  // BUGS.md M56 (Chats brief CT1). Mike, 16 Oct 2026: "it shouldn't pick a conversation. It should always go to the most
  // recent conversation [...] like Teams would." With no conversation in the address, the two-pane layout opens the top
  // row of the list as it stands (newest first, his narrowing applied), with replace so Back does not loop. Closing New
  // comes back here the same way. One pane on a phone keeps the list first, as Teams on a phone does.
  const newest = shown[0]?.id ?? null;
  useEffect(() => {
    if (id || narrow || creating || waitingFor || !view || !newest) return;
    go(at(`/chats/${newest}`), { replace: true });
  }, [id, narrow, creating, waitingFor, view, newest]);
  const counts = view?.counts ?? { cobrain: 0, teams: 0, external: 0 };
  const total = counts.cobrain + counts.teams;
  // What he has, not where we keep it -- and no segment that reads "0 of something". When a chip or the
  // find field is narrowing the list, the count leads with what is IN it: the store's 573 standing over a
  // list of 8, with nothing saying so, is the same fault as counting a page and calling it the store.
  const narrowed = filter !== 'all' || find.trim() !== '' || f.company !== '';
  const sub = view
    ? [
        narrowed
          ? `${shown.length.toLocaleString()} of ${total.toLocaleString()} conversation${total === 1 ? '' : 's'}`
          : `${total.toLocaleString()} conversation${total === 1 ? '' : 's'}`,
        counts.external > 0 ? `${counts.external.toLocaleString()} with someone external` : null,
        view.builtAt ? `Teams up to ${whenWords(view.builtAt)}` : null,
      ].filter(Boolean).join(' · ')
    : 'Reading your conversations…';

  const chip = (k: Filter, label: ReactNode, extra = '') => (
    <ActionButton reading type="button" className={`ch-chip${extra ? ` ${extra}` : ''}${filter === k ? ' on' : ''}`} onClick={() => setFilter(k)}>{label}</ActionButton>
  );

  return (
    <div className="chats">
      <div className="ch-top">
        <h1 className="ch-title">Chats</h1>
        <span className="ch-sub">{sub}</span>
      </div>
      {error ? <p className="ch-error" role="alert">{error.charAt(0).toUpperCase()}{error.slice(1)}. <ActionButton reading type="button" className="dotted" onClick={() => void refresh()}>Try again</ActionButton></p> : null}
      {/* The count stays in the heading, where it has always been; the bar draws none of its own. */}
      <FilterBar find="Find a conversation" q={find} onFind={(q) => setF({ q })} shown={null} total={null} noun={['conversation', 'conversations']}
        onClearAll={() => setF({ q: '', show: '', company: '' })}
        extraSet={filter !== 'all'}
        extra={<>
          {chip('all', 'All')}
          {chip('cobrain', 'Cobrain')}
          {chip('teams', 'Teams')}
          {chip('external', <>{EXTERNAL_WORD}<Warn /></>, 'out')}
        </>}
        picks={[{ label: 'By company', find: 'Find a company', any: 'Any company', value: f.company, options: view ? tally(chipped, (c) => c.companies ?? []) : null, onChange: (company) => setF({ company }) }]} />
      <div ref={splitRef} className={selected && narrow ? 'ch-split one' : 'ch-split'} style={fitHeight ? ({ height: `${fitHeight}px` } as CSSProperties) : undefined}>
        {!(selected && narrow) ? (
          <div className="ch-col">
          {/* BUGS.md M59 (Chats brief CT4). Mike: "It's in the wrong place"; asked where, "the same as the guidance
              provided by teams": Teams' new-chat icon at the head of the chat list, under its own name. */}
          <div className="ch-col-h">
            <ActionButton type="button" className="ch-new" aria-label="New chat" title="New chat" onClick={() => { setCreating(true); go(at('/chats')); }}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" /><path d="M17.5 3.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z" /></svg>
            </ActionButton>
          </div>
          <div className="ch-list" role="list" aria-label="Conversations">
            {/* Three states, never one: a list that has not loaded is not an empty list, and an empty list
                with an error above it is not "no conversations yet". */}
            {!view && !error ? <p className="note ch-pad">Reading your conversations…</p> : null}
            {view && shown.length === 0 ? (
              <p className="note ch-pad">
                {rows.length === 0
                  ? 'No conversations yet. Your Teams chats appear here once Cobrain has read them; you can start a chat with a colleague now.'
                  : find.trim() || f.company
                    ? find.trim() ? `Nothing matches “${find.trim()}”.` : 'No conversation with that company.'
                    : filter === 'cobrain' ? 'No Cobrain chats.' : filter === 'teams' ? 'Nothing from Teams.' : 'Nothing with someone external.'}
              </p>
            ) : null}
            {shown.map((c) => (
              <Line key={`${c.source}:${c.id}`} c={c} on={c.id === id} myName={me?.displayName ?? null} showSource={counts.cobrain > 0 && counts.teams > 0} onOpen={() => { setCreating(false); go(at(`/chats/${c.id}`)); }} />
            ))}

          </div>
          </div>
        ) : null}
        {creating ? (
          <div className="ch-thread">
            <NewTeamsGroup store={tenant} onClose={() => setCreating(false)}
              onMade={(made) => { setCreating(false); setWaitingFor({ made, knew: teamsIdsNow() }); }}
              onOpen={(threadId) => { setCreating(false); go(at(`/chats/${threadId}`)); }} />
          </div>
        ) : waitingFor ? (
          <div className="ch-thread"><GroupArriving made={waitingFor.made} onGiveUp={() => setWaitingFor(null)} /></div>
        ) : selected && selected.source === 'teams' ? (
          <TeamsThread key={`${tenant}:${selected.id}`} store={tenant} row={selected} myName={me?.displayName ?? null} onBack={() => go(at('/chats'))} onSent={() => void refresh()} />
        ) : selected && me ? (
          <ChatView key={selected.id} store={tenant} me={me} id={selected.id} onChanged={() => void refresh()} canManage {...(narrow ? { onBack: () => go(at('/chats')) } : {})} />
        ) : id && !view && !error ? (
          // BUGS.md M35: a chat opened from its address waits for the list, which says whether it is a Teams chat or a
          // Cobrain one. Asking the messenger first got a 404 for every Teams chat opened by a reload or a link.
          <div className="ch-thread"><p className="note ch-pad">Reading your conversations…</p></div>
        ) : id && me ? (
          // A chat not on the list, one started a moment ago or a list that could not be read: open it as a Cobrain
          // conversation rather than showing "pick one". An id that is neither gets the API's own words.
          <ChatView key={id} store={tenant} me={me} id={id} onChanged={() => void refresh()} canManage {...(narrow ? { onBack: () => go(at('/chats')) } : {})} />
        ) : (
          // While the newest is being opened (M56) the pane is empty for that moment rather than asking him to pick.
          <div className="ch-thread">{!narrow && newest ? null : <p className="note ch-pad">{rows.length ? 'Pick a conversation.' : 'Nothing to read yet.'}</p>}</div>
        )}
      </div>
    </div>
  );
}
